import { useEffect, useMemo, useState } from 'react';
import { wordpressAPI } from '../services/wordpressApi';
import Breadcrumb from '../components/layout/Breadcrumb';
import Input from '../components/ui/Input';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import { decodeHtmlEntities } from '../utils/htmlEntities';
import { usePageTitle } from '../hooks/usePageTitle';

const GLOSSARY_SLUG = 'glossary-of-terms-definition-and-acronyms';

interface GlossaryEntry {
  term: string;
  definition: string;
}

interface GlossarySection {
  title: string;
  level: 2 | 3;
  entries: GlossaryEntry[];
}

interface GlossaryDocument {
  title: string;
  sections: GlossarySection[];
  references: string[];
}

function cleanText(value: string | null | undefined) {
  return (value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function parseGlossary(html: string): Omit<GlossaryDocument, 'title'> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sections: GlossarySection[] = [];
  let current: GlossarySection | null = null;
  let inReferences = false;
  const references: string[] = [];

  doc.body.querySelectorAll('h2, h3, table, ol').forEach((node) => {
    if (node.tagName === 'H2' || node.tagName === 'H3') {
      const title = cleanText(node.textContent);
      inReferences = /references/i.test(title);
      if (!inReferences && title) {
        current = { title, level: node.tagName === 'H2' ? 2 : 3, entries: [] };
        sections.push(current);
      }
      return;
    }

    if (node.tagName === 'OL' && inReferences) {
      node.querySelectorAll('li').forEach((item) => {
        const text = cleanText(item.textContent);
        if (text) references.push(text);
      });
      return;
    }

    if (node.tagName !== 'TABLE' || !current) return;

    node.querySelectorAll('tr').forEach((row) => {
      const cells = Array.from(row.querySelectorAll('th, td')).map((cell) => cleanText(cell.textContent));
      if (cells.length < 2 || !cells[0] || !cells[1]) return;
      if (/^term$/i.test(cells[0]) && /^definition$/i.test(cells[1])) return;
      current?.entries.push({ term: cells[0], definition: cells[1] });
    });
  });

  return { sections, references };
}

export default function Glossary() {
  const [document, setDocument] = useState<GlossaryDocument | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle(document ? decodeHtmlEntities(document.title) : 'Glossary');

  const load = () => {
    setLoading(true);
    wordpressAPI
      .fetchPostBySlug(GLOSSARY_SLUG)
      .then((post) => {
        if (!post) {
          setDocument(null);
          setError(new Error('Glossary not found'));
          return;
        }
        const parsed = parseGlossary(post.content.rendered);
        setDocument({ title: decodeHtmlEntities(post.title.rendered), ...parsed });
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load glossary')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = useMemo(() => {
    if (!document) return [];
    const sections = normalizedQuery
      ? document.sections.map((section) => ({
          ...section,
          entries: section.entries.filter(
            (entry) =>
              entry.term.toLowerCase().includes(normalizedQuery) ||
              entry.definition.toLowerCase().includes(normalizedQuery)
          ),
        }))
      : document.sections;
    const visible = new Set<number>();
    sections.forEach((section, index) => {
      if (section.entries.length === 0) return;
      visible.add(index);
      for (let previous = index - 1; previous >= 0; previous -= 1) {
        if (sections[previous].level === 2) {
          visible.add(previous);
          break;
        }
      }
    });
    return sections.filter((_, index) => visible.has(index));
  }, [document, normalizedQuery]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Glossary' }]} />
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        {document?.title || 'Glossary'}
      </h1>
      {loading && <LoadingSkeleton lines={8} />}
      {error && <ErrorState error={error} title="Could not load the glossary" onRetry={load} />}
      {document && !loading && !error && (
        <>
          <div className="mb-8 max-w-xl">
            <Input
              label="Search terms"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search terms and acronyms"
              type="search"
            />
          </div>
          {visibleSections.length === 0 && (
            <EmptyState
              title="No matching terms"
              description="Nothing in the glossary matches that search."
            />
          )}
          {visibleSections.map((section) => (
            <section key={section.title} className="mb-10">
              {section.level === 2 ? (
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
              ) : (
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h3>
              )}
              {section.entries.length > 0 && (
                <dl className="divide-y divide-gray-200 border-t border-gray-200">
                  {section.entries.map((entry) => (
                    <div key={`${section.title}-${entry.term}`} className="py-4">
                      <dt className="font-semibold text-gray-900">{entry.term}</dt>
                      <dd className="text-gray-700 mt-1">{entry.definition}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          ))}
          {!normalizedQuery && document.references.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. REFERENCES</h2>
              <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                {document.references.map((reference) => (
                  <li key={reference}>{reference}</li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
    </div>
  );
}
